import type { z } from 'zod';
import type { Locale } from '../paraglide/runtime.js';
import { baseSearchSchema } from './relativePath';

export type Layout = 'authenticated' | 'public' | 'bare';

/** Path constraint: must start with a slash. Catches typos at definePath callsites. */
export type AbsolutePath = `/${string}`;

/** Literal path TanStack expects on an index child route (renders at its parent's URL). */
export type IndexPath = '/';

type AnyZodType = z.ZodType;
type AnyZodObject = z.ZodObject;
type BaseSearchShape = (typeof baseSearchSchema)['shape'];

/**
 * A declared search schema plus the params every route accepts ({@link baseSearchSchema}).
 * Falls back to the declared schema for anything that isn't an object schema, which cannot be
 * extended — and a path declaring no schema keeps TanStack's unvalidated passthrough, which carries
 * the base params just as well.
 */
export type WithBaseSearch<S extends AnyZodType | undefined> =
  S extends z.ZodObject<infer Shape, infer Config> ? z.ZodObject<Shape & BaseSearchShape, Config> : S;

export interface DefinedPath<
  L extends Layout = Layout,
  P extends AbsolutePath = AbsolutePath,
  S extends AnyZodType | undefined = AnyZodType | undefined,
  Pa extends AnyZodType | undefined = AnyZodType | undefined,
> {
  /** Canonical path used as the TanStack route pattern. */
  path: P;
  /** Layout-prefixed route id. (Read search via `useRouteSearch`, not `useSearch({ from: id })` — the tree is `AnyRoute`-typed.) */
  id: `/_${L}${P}`;
  /** Layout this route lives under. Used by `definePageRoute` to pick the right parent. */
  layout: L;
  /** Per-locale URL translations consumed by paraglide URL patterns. */
  translations: Record<Locale, string>;
  /** Zod schema for `validateSearch`, declared alongside the path. TanStack Router consumes zod v4 schemas natively. */
  search: S;
  /** Zod schema for `params`, declared alongside the path. */
  params: Pa;
}

/**
 * The validated `search` shape a path's schema produces, or `unknown` when the path declares
 * none. Lets `definePageRoute` type its `loaderDeps` argument off the schema `definePath`
 * already captured, instead of falling back to `any`.
 */
export type SearchOf<S extends AnyZodType | undefined> = S extends AnyZodType ? z.infer<S> : unknown;

/** The pathless layout route's id, which every path id under that layout is prefixed with. */
export function layoutRouteId<L extends Layout>(layout: L): `_${L}` {
  return `_${layout}`;
}

/**
 * Single source of truth for a route's path, layout-prefixed id, locale URL translations,
 * and optional `search`/`params` schemas. The `en` translation is treated as canonical
 * (TanStack matches on this), while `de` (and any future locale) are translations paraglide
 * rewrites at the URL boundary. The id is derived from the layout + canonical path so it can
 * never drift out of sync with the path.
 *
 * Required keys on `translations` track `Locale` from paraglide — adding a third locale to the
 * inlang project surfaces missing-translation errors here, not silent gaps in URL routing.
 *
 * `schemas.search` / `schemas.params` are stored on the result; `definePageRoute` auto-wires
 * them as `validateSearch` / `params` so the schema lives next to the path declaration
 * instead of in the manifest. TanStack Router consumes zod v4 schemas directly — no adapter.
 *
 * A declared `search` schema is extended with {@link baseSearchSchema}, the params every route
 * accepts, so no path has to restate them and none silently strips one.
 */
export function definePath<
  const L extends Layout,
  const P extends AbsolutePath,
  const S extends AnyZodType | undefined = undefined,
  const Pa extends AnyZodType | undefined = undefined,
>(
  layout: L,
  translations: Record<Locale, string> & { en: P },
  schemas?: { search?: S; params?: Pa },
): DefinedPath<L, P, WithBaseSearch<S>, Pa> {
  return {
    path: translations.en,
    id: `/${layoutRouteId(layout)}${translations.en}`,
    layout,
    translations,
    search: withBaseSearch(schemas?.search),
    params: schemas?.params as Pa,
  };
}

/**
 * Adds the base params a declared schema doesn't already define. Extends the declared schema rather
 * than the base one, both because the declaration wins on a shared key and because `extend` carries
 * the receiver's config — extending the other way around would turn a `looseObject` into a stripping
 * one and drop the attribution params some flows carry through.
 */
function withBaseSearch<S extends AnyZodType | undefined>(declared: S | undefined): WithBaseSearch<S> {
  if (declared === undefined || !isZodObject(declared)) {
    return declared as WithBaseSearch<S>;
  }
  const missing = Object.fromEntries(Object.entries(baseSearchSchema.shape).filter(([key]) => !(key in declared.shape)));
  return declared.extend(missing) as unknown as WithBaseSearch<S>;
}

function isZodObject(schema: AnyZodType): schema is AnyZodObject {
  return schema.def.type === 'object';
}

/**
 * Produces a `DefinedPath` for an index child route (TanStack pattern `path: '/'`
 * rendering at the parent's URL). TanStack composes the runtime route id by
 * appending a trailing slash to the parent's id (verified at runtime), so this
 * helper does the same. `translations` mirror the parent so URL rewriting still
 * resolves to the parent's localized path.
 *
 * Pair with `definePageRoute({ parent: layoutRoute, path: indexPath, ... })`.
 */
export function defineIndexPath<
  const L extends Layout,
  const P extends AbsolutePath,
  const S extends AnyZodType | undefined = undefined,
  const Pa extends AnyZodType | undefined = undefined,
>(parent: DefinedPath<L, P, S, Pa>): Omit<DefinedPath<L, P, S, Pa>, 'path' | 'id'> & { path: IndexPath; id: `/_${L}${P}/` } {
  return {
    path: '/',
    id: `${parent.id}/`,
    layout: parent.layout,
    translations: parent.translations,
    search: parent.search,
    params: parent.params,
  };
}
